import {
  Box,
  Button,
  createStyles,
  makeStyles,
  MenuItem,
  Select,
} from "@material-ui/core";
import ActionInliner from "legacy-components/form/action/ActionInliner";
import FormAlert from "legacy-components/form/container/FormAlert";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import Alert from "legacy-components/alert/Alert";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import ApiService from "../../services/ApiService";
import { UpdateDeploymentProps } from "../../components/DeploymentDetails";

const useStyles = makeStyles(() =>
  createStyles({
    formLayout: {
      display: "flex",
      width: "100%",
    },
    formContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    submitLoader: {
      position: "absolute",
      zIndex: 2,
      width: "100%",
      height: "100%",
      backgroundColor: "rgb(255, 255, 255, 0.32)",
    },
  })
);

export interface UpdateDeploymentFormLegacyProps {
  onCancel: () => void;
  onSuccess?: () => void;
  basePath: string;
  version: string;
  availableRegions: { key: string; value: string }[];
  updateDeploymentInitialValue?: UpdateDeploymentProps;
}

/** As on `develop`: PUT .../deployment/{id} via ApiService.updateDeployment */
const UpdateDeploymentFormLegacy: React.FC<UpdateDeploymentFormLegacyProps> = ({
  onCancel,
  onSuccess,
  availableRegions,
  updateDeploymentInitialValue,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const [success, setSuccess] = useState(false);
  const [loader, setLoader] = useState(false);

  const FormErrorViewer = ({ errors }: { errors?: string[] }) => {
    return (
      <>
        {errors?.map((error, index) => (
          <div key={index}>{error}</div>
        ))}
      </>
    );
  };

  const [error, setError] = useState({
    is_error: false,
    message: "",
  });
  const [formValues, setFormValues] = useState({
    region: updateDeploymentInitialValue?.region ?? "",
    targetEndpoint: updateDeploymentInitialValue?.targetEndpoint ?? "",
    timeout:
      updateDeploymentInitialValue?.timeout !== undefined &&
      updateDeploymentInitialValue?.timeout !== null
        ? String(updateDeploymentInitialValue.timeout)
        : "",
  });
  const [hasFieldError, setHasFieldError] = useState(false);
  const [hasFieldError2, setHasFieldError2] = useState(false);

  const handleSetError2 = (value: boolean) => {
    setHasFieldError2(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const { state: updateDeploymentState, subscribe: updateDeployment } =
    useAsyncAction(() =>
      ApiService.updateDeployment(
        {
          backendUrl: formValues.targetEndpoint,
          timeout: parseInt(formValues.timeout.toString(), 10) || 30,
        },
        updateDeploymentInitialValue?.deploymentId ?? ""
      )
    );

  const handleSubmit = () => {
    updateDeployment();
  };

  useEffect(() => {
    const isLoaded = updateDeploymentState?.loaded;
    if (isLoaded && onSuccess) {
      onSuccess();
    }
  }, [updateDeploymentState, onSuccess]);

  return (
    <form
      className={styles.formLayout}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Box className={styles.formContainer}>
        {loader && <Box className={styles.submitLoader}></Box>}
        {error.is_error && (
          <FormAlert severity="error">
            <FormErrorViewer errors={[error.message]} />
          </FormAlert>
        )}
        {success && (
          <FormAlert severity="success">
            {"Deployment started Successfully"}
          </FormAlert>
        )}
        {updateDeploymentState?.error && <Alert id="CREATEDEPLOYMENT" />}
        <Box
          style={{
            color: "#464B52",
            fontSize: "14px",
            lineHeight: "21px",
            paddingBottom: "7px",
            fontWeight: 600,
          }}
        >
          Region&nbsp;<span style={{ color: "#DA212C" }}>*</span>
        </Box>
        <Select
          id="regions"
          IconComponent={ExpandMoreIcon}
          variant="outlined"
          className="header-selector resource-version-select"
          placeholder="Select the region you want to deploy"
          style={{ minWidth: "15%" }}
          value={formValues.region}
          disabled={true}
        >
          {availableRegions?.map((reg) => (
            <MenuItem value={reg.value} key={reg.value}>
              {reg.key}
            </MenuItem>
          ))}
        </Select>

        <InputContainer
          label={intl.formatMessage({
            defaultMessage: "Target Endpoint",
          })}
          placeholder={intl.formatMessage({
            defaultMessage: "Enter the target backend endpoint",
          })}
          value={formValues.targetEndpoint}
          onChange={handleChange}
          name={"targetEndpoint"}
          setError={handleSetError2}
          validate={{
            required: true,
            pattern:
              "^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)[a-z0-9]+([-.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/[^?]*)?(\\?([a-zA-Z0-9_-]+=[a-zA-Z0-9_-]+)(&[a-zA-Z0-9_-]+=[a-zA-Z0-9_-]+)*)?$",
          }}
        />

        <InputContainer
          label={intl.formatMessage({
            defaultMessage: "Consumption Endpoint",
          })}
          value={updateDeploymentInitialValue?.deployedEndpoint?.toLowerCase()}
          disabled
          onChange={handleChange}
          name={"consumptionEndpoint"}
          validate={{
            required: true,
            minLength: 3,
            maxLength: 64,
            allowSpace: true,
            pattern: "^[a-zA-Z0-9-_ ]+$",
          }}
          setError={handleSetError2}
          required
        />
        <InputContainer
          label={intl.formatMessage({
            defaultMessage: "Connect Timeout",
          })}
          placeholder={intl.formatMessage({
            defaultMessage: "Enter the connection timeout",
          })}
          value={formValues.timeout.toString()}
          onChange={handleChange}
          name={"timeout"}
          validate={{
            required: true,
            allowSpace: false,
            pattern: "^[0-9]+$",
            min: 1,
            max: 55,
            patternMessage: "Only numeric values are allowed",
          }}
          setError={handleSetError2}
          required
        />

        <ActionInliner padding="inset-top">
          <Button
            onClick={onCancel}
            type="button"
            variant="outlined"
            disableElevation
          >
            {" "}
            <FormattedMessage defaultMessage="Cancel" />
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disableElevation
            disabled={
              hasFieldError || hasFieldError2 || !formValues.targetEndpoint
            }
          >
            <FormattedMessage defaultMessage="Update" />
          </Button>
        </ActionInliner>
        <Box pb={2} />
      </Box>
    </form>
  );
};

export default UpdateDeploymentFormLegacy;
